import React, { useEffect, useRef, useState } from 'react';
import { fromBlob } from 'image-resize-compress';
import './UnitEditor.css';
import { DEFAULT_TAX_RATE, linkPricing, originalPriceWarning } from '../pricing/pricingLinks';
import UnitSpecialPrices, { ruleProblem } from './UnitSpecialPrices';

// =========================================================
// CONSTANTS
// =========================================================

const UNIT_TYPES = [
    { value: 'entire_property', label: 'Entire Property' },
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'room', label: 'Room' },
    { value: 'suite', label: 'Suite' },
    { value: 'dorm', label: 'Dormitory' },
    { value: 'cottage', label: 'Cottage' },
    { value: 'tent', label: 'Tent' },
    { value: 'campsite', label: 'Campsite' },
    { value: 'cabin', label: 'Cabin' },
    { value: 'treehouse', label: 'Treehouse' },
    { value: 'bed', label: 'Bed' },
    { value: 'other', label: 'Other' }
];

const BOOKING_TYPES = [
    {
        value: 'entire_unit',
        label: 'Entire Unit',
        description: 'Guests book the complete unit.'
    },
    {
        value: 'private_unit',
        label: 'Private Unit',
        description: 'Private accommodation for guests.'
    },
    {
        value: 'shared_unit',
        label: 'Shared Unit',
        description: 'Guests share the accommodation.'
    },
    {
        value: 'bed',
        label: 'Individual Bed',
        description: 'Guests book individual beds.'
    },
    {
        value: 'campsite',
        label: 'Campsite',
        description: 'Guests reserve a campsite.'
    }
];

// values = the backend's allowed values (model/accommodation/accommodation.js)
const BHK_TYPES = [
    { value: 'studio', label: 'Studio' },
    { value: '1BHK', label: '1 BHK' },
    { value: '2BHK', label: '2 BHK' },
    { value: '3BHK', label: '3 BHK' },
    { value: '4BHK', label: '4 BHK' },
    { value: '5BHK', label: '5+ BHK' },
    { value: 'other', label: 'Other' }
];

const SHARING_TYPES = [
    { value: 'private', label: 'Private' },
    { value: '1_sharing', label: 'Single Sharing' },
    { value: '2_sharing', label: 'Twin Sharing' },
    { value: '3_sharing', label: 'Triple Sharing' },
    { value: '4_sharing', label: 'Quad Sharing' },
    { value: '6_sharing', label: '6 Sharing' },
    { value: '8_sharing', label: '8 Sharing (Dormitory)' }
];

// values = the backend tent types (model/accommodation/listing.js tentSchema)
const TENT_TYPES = [
    { value: 'swiss', label: 'Swiss Tent' },
    { value: 'dome', label: 'Dome Tent' },
    { value: 'alpine', label: 'Alpine Tent' },
    { value: 'bell', label: 'Bell Tent' },
    { value: 'a_frame', label: 'A-Frame Tent' },
    { value: 'safari', label: 'Safari Tent' },
    { value: 'canvas', label: 'Canvas Tent' },
    { value: 'geodesic', label: 'Geodesic Dome' },
    { value: 'luxury', label: 'Luxury Tent' },
    { value: 'other', label: 'Other' }
];

const VIEWS = [
    'Mountain View',
    'Valley View',
    'Forest View',
    'Garden View',
    'Lake View',
    'River View',
    'Pool View',
    'City View',
    'Beach View',
    'No Special View'
];

const BED_TYPES = [
    'Single Bed',
    'Twin Bed',
    'Double Bed',
    'Queen Bed',
    'King Bed',
    'Bunk Bed',
    'Sofa Bed',
    'Floor Mattress',
    'Sleeping Bag',
    'Other'
];

const UNIT_AMENITIES = [
    'WiFi',
    'Air Conditioning',
    'Heating',
    'TV',
    'Refrigerator',
    'Kitchen',
    'Microwave',
    'Dining Area',
    'Private Bathroom',
    'Hot Water',
    'Balcony',
    'Workspace',
    'Wardrobe',
    'Room Service',
    'Breakfast',
    'Parking',
    'Power Backup',
    'Fire Extinguisher'
];

// =========================================================
// PHOTO SETTINGS
// =========================================================

const MAX_UNIT_IMAGES = 10;
const MAX_UNIT_IMAGE_SIZE = 10 * 1024 * 1024;
const COMPRESSED_IMAGE_TARGET = 2 * 1024 * 1024;

// =========================================================
// EMPTY UNIT
// =========================================================

const createEmptyUnit = () => ({
    name: '',
    shortName: '',
    description: '',

    unitType: 'room',
    bookingType: 'private_unit',

    bhkType: '',
    sharingType: '',

    occupancy: {
        maxGuests: 2,
        adults: 2,
        children: 0,
        infants: 0
    },

    bedrooms: 1,

    beds: [
        {
            type: 'Double Bed',
            count: 1
        }
    ],

    bathrooms: 1,

    sizeSqft: 0,
    floor: '',
    view: '',

    amenities: [],

    tent: {
        tentType: '',
        material: '',
        hasElectricity: false,
        hasPrivateBathroom: false,
        hasAttachedBathroom: false
    },

    inventory: {
        totalUnits: 1,
        activeUnits: 1,
        maintenanceUnits: 0,
        closed: false
    },

    pricing: {
        name: '',
        terms: '',
        actual: 0,
        tax: 0,
        total: 0,
        higherPrice: 0,
        discountPercent: 0,
        reserveAmount: 0,
        currency: 'INR',
        unit: 'per_night'
    },

    pricingRules: [],

    services: [],

    minGuests: 1,
    maxGuests: 2,

    images: [],

    active: true,
    displayOrder: 0
});

// =========================================================
// COMPONENT
// =========================================================

const UnitEditor = ({ unit = null, index = null, onSave, onCancel, canEdit = true }) => {
    const [formData, setFormData] = useState(unit || createEmptyUnit());

    const [activeTab, setActiveTab] = useState('basic');

    const [errors, setErrors] = useState({});

    // Photo state
    const [isCompressing, setIsCompressing] = useState(false);

    const [draggedImageIndex, setDraggedImageIndex] = useState(null);

    const fileInputRef = useRef(null);

    // =====================================================
    // LOAD UNIT
    // =====================================================

    useEffect(() => {
        const normalizedUnit = unit || createEmptyUnit();

        setFormData({
            ...createEmptyUnit(),
            ...normalizedUnit,

            occupancy: {
                ...createEmptyUnit().occupancy,
                ...(normalizedUnit.occupancy || {})
            },

            inventory: {
                ...createEmptyUnit().inventory,
                ...(normalizedUnit.inventory || {})
            },

            pricing: {
                ...createEmptyUnit().pricing,
                ...(normalizedUnit.pricing || {})
            },

            tent: {
                ...createEmptyUnit().tent,
                ...(normalizedUnit.tent || {})
            },

            beds: normalizedUnit.beds || [],

            amenities: normalizedUnit.amenities || [],

            images: normalizedUnit.images || []
        });
    }, [unit]);

    // =====================================================
    // UPDATE FIELD
    // =====================================================

    const updateField = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    // =====================================================
    // UPDATE NESTED FIELD
    // =====================================================

    const updateNestedField = (parent, field, value) => {
        setFormData((prev) => ({
            ...prev,

            [parent]: {
                ...prev[parent],
                [field]: value
            }
        }));
    };

    // =====================================================
    // OCCUPANCY
    // =====================================================

    const updateOccupancy = (field, value) => {
        updateNestedField('occupancy', field, value === '' ? '' : Number(value));
    };

    // =====================================================
    // INVENTORY
    // =====================================================

    const updateInventory = (field, value) => {
        updateNestedField('inventory', field, value === '' ? '' : Number(value));
    };

    // =====================================================
    // PRICING
    // =====================================================

    // base price / tax / total / original price / discount fill each other in
    const updatePricing = (field, value) => {
        if (['actual', 'tax', 'total', 'higherPrice', 'discountPercent'].includes(field)) {
            setFormData((prev) => ({ ...prev, pricing: linkPricing(prev.pricing || {}, field, value) }));
            return;
        }
        updateNestedField('pricing', field, value === '' ? '' : Number(value));
    };

    // =====================================================
    // TENT
    // =====================================================

    const updateTent = (field, value) => {
        updateNestedField('tent', field, value);
    };

    // =====================================================
    // BEDS
    // =====================================================

    const addBed = () => {
        setFormData((prev) => ({
            ...prev,

            beds: [
                ...(prev.beds || []),
                {
                    type: 'Double Bed',
                    count: 1
                }
            ]
        }));
    };

    const updateBed = (bedIndex, field, value) => {
        setFormData((prev) => ({
            ...prev,

            beds: prev.beds.map((bed, currentIndex) =>
                currentIndex === bedIndex
                    ? {
                          ...bed,
                          [field]: field === 'count' ? Number(value) : value
                      }
                    : bed
            )
        }));
    };

    const removeBed = (bedIndex) => {
        setFormData((prev) => ({
            ...prev,

            beds: prev.beds.filter((_, currentIndex) => currentIndex !== bedIndex)
        }));
    };

    // =====================================================
    // AMENITIES
    // =====================================================

    const toggleAmenity = (amenity) => {
        setFormData((prev) => {
            const current = prev.amenities || [];

            const exists = current.includes(amenity);

            return {
                ...prev,

                amenities: exists ? current.filter((item) => item !== amenity) : [...current, amenity]
            };
        });
    };

    // =====================================================
    // IMAGE HELPERS
    // =====================================================

    const getImageUrl = (image) => {
        if (!image) return '';

        if (typeof image === 'string') {
            return image;
        }

        if (image.preview) {
            return image.preview;
        }

        if (image.url) {
            return image.url;
        }

        if (image.src) {
            return image.src;
        }

        return '';
    };

    const isExistingImage = (image) => {
        if (!image) return false;

        if (typeof image === 'string') {
            return true;
        }

        return image.isExisting === true || (!image.file && Boolean(image.url));
    };

    // =====================================================
    // COMPRESS IMAGE
    // =====================================================

    const compressUnitImage = async (file) => {
        try {
            const compressed = await fromBlob(file, 80, 1600, 1600);

            /*
             * fromBlob can return a Blob.
             *
             * Convert it back to File so our
             * Firebase upload layer can use it.
             */

            if (compressed instanceof File) {
                return compressed;
            }

            return new File([compressed], file.name, {
                type: compressed.type || file.type,
                lastModified: Date.now()
            });
        } catch (error) {
            console.error('Unit image compression failed:', error);

            return file;
        }
    };

    // =====================================================
    // ADD PHOTOS
    // =====================================================

    const handleImageSelect = async (event) => {
        const files = Array.from(event.target.files || []);

        if (!files.length) {
            return;
        }

        if (!canEdit) {
            return;
        }

        const currentImages = formData.images || [];

        const remainingSlots = MAX_UNIT_IMAGES - currentImages.length;

        if (remainingSlots <= 0) {
            setErrors((prev) => ({
                ...prev,
                images: `Maximum ${MAX_UNIT_IMAGES} images allowed.`
            }));

            event.target.value = '';
            return;
        }

        const selectedFiles = files.slice(0, remainingSlots);

        const invalidFiles = selectedFiles.filter((file) => {
            if (!file.type.startsWith('image/')) {
                return true;
            }

            if (file.size > MAX_UNIT_IMAGE_SIZE) {
                return true;
            }

            return false;
        });

        if (invalidFiles.length) {
            setErrors((prev) => ({
                ...prev,

                images: 'Only image files up to 10MB each are allowed.'
            }));

            event.target.value = '';
            return;
        }

        setErrors((prev) => {
            const next = {
                ...prev
            };

            delete next.images;

            return next;
        });

        try {
            setIsCompressing(true);

            const newImages = [];

            for (const file of selectedFiles) {
                const compressedFile = await compressUnitImage(file);

                const previewUrl = URL.createObjectURL(compressedFile);

                newImages.push({
                    file: compressedFile,

                    preview: previewUrl,

                    isExisting: false,

                    name: compressedFile.name,

                    size: compressedFile.size,

                    type: compressedFile.type
                });
            }

            setFormData((prev) => ({
                ...prev,

                images: [...(prev.images || []), ...newImages]
            }));
        } catch (error) {
            console.error('Failed to add unit images:', error);

            setErrors((prev) => ({
                ...prev,

                images: 'Unable to process one or more images.'
            }));
        } finally {
            setIsCompressing(false);

            event.target.value = '';
        }
    };

    // =====================================================
    // REMOVE IMAGE
    // =====================================================

    const removeImage = (imageIndex) => {
        if (!canEdit) {
            return;
        }

        setFormData((prev) => {
            const image = prev.images?.[imageIndex];

            /*
             * Revoke blob URL for newly selected
             * images to avoid memory leaks.
             */

            if (image && !isExistingImage(image) && image.preview) {
                URL.revokeObjectURL(image.preview);
            }

            return {
                ...prev,

                images: prev.images.filter((_, index) => index !== imageIndex)
            };
        });
    };

    // =====================================================
    // DRAG START
    // =====================================================

    const handleImageDragStart = (index) => {
        if (!canEdit) {
            return;
        }

        setDraggedImageIndex(index);
    };

    // =====================================================
    // DRAG OVER
    // =====================================================

    const handleImageDragOver = (event) => {
        event.preventDefault();
    };

    // =====================================================
    // DROP / REORDER
    // =====================================================

    const handleImageDrop = (dropIndex) => {
        if (!canEdit || draggedImageIndex === null || draggedImageIndex === dropIndex) {
            setDraggedImageIndex(null);
            return;
        }

        setFormData((prev) => {
            const images = [...(prev.images || [])];

            const draggedImage = images[draggedImageIndex];

            images.splice(draggedImageIndex, 1);

            images.splice(dropIndex, 0, draggedImage);

            return {
                ...prev,
                images
            };
        });

        setDraggedImageIndex(null);
    };

    // =====================================================
    // MOVE IMAGE LEFT / RIGHT
    // =====================================================

    const moveImage = (imageIndex, direction) => {
        if (!canEdit) {
            return;
        }

        setFormData((prev) => {
            const images = [...(prev.images || [])];

            const newIndex = direction === 'left' ? imageIndex - 1 : imageIndex + 1;

            if (newIndex < 0 || newIndex >= images.length) {
                return prev;
            }

            const temp = images[imageIndex];

            images[imageIndex] = images[newIndex];

            images[newIndex] = temp;

            return {
                ...prev,
                images
            };
        });
    };

    // =====================================================
    // CLEANUP PREVIEWS
    // =====================================================

    useEffect(() => {
        return () => {
            /*
             * Revoke only previews belonging
             * to newly selected files.
             */
            (formData.images || []).forEach((image) => {
                if (image && !isExistingImage(image) && image.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        };
    }, []);

    // =====================================================
    // VALIDATE
    // =====================================================

    const validate = () => {
        const newErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Unit name is required.';
        }

        if (!formData.unitType) {
            newErrors.unitType = 'Unit type is required.';
        }

        if (!formData.occupancy?.maxGuests || Number(formData.occupancy.maxGuests) <= 0) {
            newErrors.maxGuests = 'Maximum guests must be greater than 0.';
        }

        if (!formData.inventory?.totalUnits || Number(formData.inventory.totalUnits) <= 0) {
            newErrors.totalUnits = 'Total units must be greater than 0.';
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // =====================================================
    // SAVE
    // =====================================================

    const handleSave = () => {
        if (!canEdit) {
            return;
        }

        if (!validate()) {
            setActiveTab('basic');
            return;
        }

        // a half-filled special price would be ignored at booking — fix it first
        if ((formData.pricingRules || []).some((rule) => ruleProblem(rule))) {
            setActiveTab('pricing');
            setErrors((prev) => ({ ...prev, pricingRules: 'Please complete or remove the special prices marked in red.' }));
            return;
        }

        /*
         * formData.images contains:
         *
         * Existing:
         * {
         *   url: "...",
         *   isExisting: true
         * }
         *
         * New:
         * {
         *   file: File,
         *   preview: "blob:...",
         *   isExisting: false
         * }
         *
         * Firebase upload will happen later
         * during accommodation submission.
         */

        onSave?.(formData, index);
    };

    const isTent = formData.unitType === 'tent' || formData.unitType === 'campsite';

    // =====================================================
    // RENDER
    // =====================================================

    return (
        <div className='unit-editor'>
            {/* =================================================
                HEADER
            ================================================= */}

            <div className='unit-editor-header'>
                <div>
                    <span className='unit-editor-label'>{index !== null ? `Unit ${index + 1}` : 'New Unit'}</span>

                    <h2>{formData.name || 'Add Accommodation Unit'}</h2>

                    <p>Configure the unit, occupancy, facilities, inventory, pricing and photos.</p>
                </div>

                <button type='button' className='unit-close-button' onClick={onCancel}>
                    ×
                </button>
            </div>

            {/* =================================================
                TABS
            ================================================= */}

            <div className='unit-editor-tabs'>
                {[
                    ['basic', 'Basic'],
                    ['occupancy', 'Occupancy'],
                    ['facilities', 'Facilities'],
                    ['inventory', 'Inventory'],
                    ['pricing', 'Pricing'],
                    ['photos', 'Photos']
                ].map(([value, label]) => (
                    <button
                        key={value}
                        type='button'
                        className={activeTab === value ? 'active' : ''}
                        onClick={() => setActiveTab(value)}
                    >
                        {label}

                        {value === 'photos' && formData.images?.length > 0 && (
                            <span className='tab-count'>{formData.images.length}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* =================================================
                CONTENT
            ================================================= */}

            <div className='unit-editor-body'>
                {/* =================================================
                    BASIC
                ================================================= */}

                {activeTab === 'basic' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Basic Unit Information</h3>

                            <p>Describe the accommodation guests will book.</p>
                        </div>

                        <div className='form-grid'>
                            <div className='form-field full-width'>
                                <label>
                                    Unit Name
                                    <span className='required'>*</span>
                                </label>

                                <input
                                    type='text'
                                    value={formData.name || ''}
                                    placeholder='e.g. Premium Swiss Tent'
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('name', e.target.value)}
                                />

                                {errors.name && <span className='field-error'>{errors.name}</span>}
                            </div>

                            <div className='form-field'>
                                <label>Short Name</label>

                                <input
                                    type='text'
                                    value={formData.shortName || ''}
                                    placeholder='e.g. Premium Tent'
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('shortName', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>
                                    Unit Type
                                    <span className='required'>*</span>
                                </label>

                                <select
                                    value={formData.unitType || ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('unitType', e.target.value)}
                                >
                                    {UNIT_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>

                                {errors.unitType && <span className='field-error'>{errors.unitType}</span>}
                            </div>

                            <div className='form-field full-width'>
                                <label>Description</label>

                                <textarea
                                    rows='5'
                                    value={formData.description || ''}
                                    placeholder='Describe this unit, its layout, views, facilities and guest experience...'
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('description', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Booking Type */}

                        <div className='unit-subsection'>
                            <div className='unit-subsection-title'>Booking Type</div>

                            <div className='booking-type-grid'>
                                {BOOKING_TYPES.map((type) => {
                                    const selected = formData.bookingType === type.value;

                                    return (
                                        <button
                                            type='button'
                                            key={type.value}
                                            className={`booking-type-card ${selected ? 'selected' : ''}`}
                                            disabled={!canEdit}
                                            onClick={() => updateField('bookingType', type.value)}
                                        >
                                            <strong>{type.label}</strong>

                                            <span>{type.description}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* BHK / Sharing */}

                        <div className='form-grid'>
                            <div className='form-field'>
                                <label>BHK Type</label>

                                <select
                                    value={formData.bhkType || ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('bhkType', e.target.value)}
                                >
                                    <option value=''>Select BHK type</option>

                                    {BHK_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className='form-field'>
                                <label>Sharing Type</label>

                                <select
                                    value={formData.sharingType || ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('sharingType', e.target.value)}
                                >
                                    <option value=''>Select sharing type</option>

                                    {SHARING_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {/* =================================================
                    OCCUPANCY
                ================================================= */}

                {activeTab === 'occupancy' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Occupancy & Layout</h3>

                            <p>Configure how many guests this unit can accommodate.</p>
                        </div>

                        <div className='form-grid four-columns'>
                            <div className='form-field'>
                                <label>
                                    Maximum Guests
                                    <span className='required'>*</span>
                                </label>

                                <input
                                    type='number'
                                    min='1'
                                    value={formData.occupancy?.maxGuests ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateOccupancy('maxGuests', e.target.value)}
                                />

                                {errors.maxGuests && <span className='field-error'>{errors.maxGuests}</span>}
                            </div>

                            <div className='form-field'>
                                <label>Adults</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.occupancy?.adults ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateOccupancy('adults', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Children</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.occupancy?.children ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateOccupancy('children', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Infants</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.occupancy?.infants ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateOccupancy('infants', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className='form-grid'>
                            <div className='form-field'>
                                <label>Bedrooms</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.bedrooms ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('bedrooms', Number(e.target.value))}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Bathrooms</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.bathrooms ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('bathrooms', Number(e.target.value))}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Size (sq.ft)</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.sizeSqft ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('sizeSqft', Number(e.target.value))}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Floor</label>

                                <input
                                    type='text'
                                    value={formData.floor || ''}
                                    placeholder='Ground Floor'
                                    disabled={!canEdit}
                                    onChange={(e) => updateField('floor', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className='form-field'>
                            <label>View</label>

                            <select
                                value={formData.view || ''}
                                disabled={!canEdit}
                                onChange={(e) => updateField('view', e.target.value)}
                            >
                                <option value=''>Select view</option>

                                {VIEWS.map((view) => (
                                    <option key={view} value={view}>
                                        {view}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Beds */}

                        <div className='unit-subsection'>
                            <div className='subsection-header'>
                                <div>
                                    <div className='unit-subsection-title'>Beds</div>

                                    <p>Configure the beds available in this unit.</p>
                                </div>

                                <button
                                    type='button'
                                    className='small-primary-button'
                                    disabled={!canEdit}
                                    onClick={addBed}
                                >
                                    + Add Bed
                                </button>
                            </div>

                            <div className='beds-list'>
                                {formData.beds?.map((bed, bedIndex) => (
                                    <div className='bed-row' key={bedIndex}>
                                        <div className='form-field'>
                                            <label>Bed Type</label>

                                            <select
                                                value={bed.type}
                                                disabled={!canEdit}
                                                onChange={(e) => updateBed(bedIndex, 'type', e.target.value)}
                                            >
                                                {BED_TYPES.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className='form-field'>
                                            <label>Quantity</label>

                                            <input
                                                type='number'
                                                min='1'
                                                value={bed.count}
                                                disabled={!canEdit}
                                                onChange={(e) => updateBed(bedIndex, 'count', e.target.value)}
                                            />
                                        </div>

                                        {canEdit && (
                                            <button
                                                type='button'
                                                className='remove-bed-button'
                                                onClick={() => removeBed(bedIndex)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* =================================================
                    FACILITIES
                ================================================= */}

                {activeTab === 'facilities' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Facilities & Amenities</h3>

                            <p>Select facilities available inside this unit.</p>
                        </div>

                        <div className='amenity-grid'>
                            {UNIT_AMENITIES.map((amenity) => {
                                const selected = formData.amenities?.includes(amenity);

                                return (
                                    <button
                                        type='button'
                                        key={amenity}
                                        className={`amenity-option ${selected ? 'selected' : ''}`}
                                        disabled={!canEdit}
                                        onClick={() => toggleAmenity(amenity)}
                                    >
                                        <span className='amenity-check'>{selected ? '✓' : ''}</span>

                                        {amenity}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Tent Details */}

                        {isTent && (
                            <div className='unit-subsection'>
                                <div className='unit-subsection-title'>Tent / Campsite Details</div>

                                <div className='form-grid'>
                                    <div className='form-field'>
                                        <label>Tent Type</label>

                                        <select
                                            value={formData.tent?.tentType || ''}
                                            disabled={!canEdit}
                                            onChange={(e) => updateTent('tentType', e.target.value)}
                                        >
                                            <option value=''>Select tent type</option>
                                            {TENT_TYPES.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className='form-field'>
                                        <label>Material</label>

                                        <input
                                            type='text'
                                            value={formData.tent?.material || ''}
                                            placeholder='Canvas'
                                            disabled={!canEdit}
                                            onChange={(e) => updateTent('material', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className='checkbox-list'>
                                    {[
                                        ['hasElectricity', 'Electricity available'],
                                        ['hasPrivateBathroom', 'Private bathroom'],
                                        ['hasAttachedBathroom', 'Attached bathroom']
                                    ].map(([field, label]) => (
                                        <label className='checkbox-option' key={field}>
                                            <input
                                                type='checkbox'
                                                checked={formData.tent?.[field] || false}
                                                disabled={!canEdit}
                                                onChange={(e) => updateTent(field, e.target.checked)}
                                            />

                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* =================================================
                    INVENTORY
                ================================================= */}

                {activeTab === 'inventory' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Unit Inventory</h3>

                            <p>Define how many units of this accommodation are available.</p>
                        </div>

                        <div className='form-grid four-columns'>
                            <div className='form-field'>
                                <label>
                                    Total Units
                                    <span className='required'>*</span>
                                </label>

                                <input
                                    type='number'
                                    min='1'
                                    value={formData.inventory?.totalUnits ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateInventory('totalUnits', e.target.value)}
                                />

                                {errors.totalUnits && <span className='field-error'>{errors.totalUnits}</span>}
                            </div>

                            <div className='form-field'>
                                <label>Active Units</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.inventory?.activeUnits ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateInventory('activeUnits', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Maintenance Units</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.inventory?.maintenanceUnits ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updateInventory('maintenanceUnits', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className='inventory-info'>
                            <strong>Availability is booking based</strong>

                            <p>
                                We will calculate available inventory from bookings instead of storing individual
                                unavailable dates inside the unit.
                            </p>
                        </div>

                        <div className='toggle-row'>
                            <div>
                                <strong>Temporarily close this unit</strong>

                                <p>Prevent new bookings without deleting the unit.</p>
                            </div>

                            <button
                                type='button'
                                className={`toggle ${formData.inventory?.closed ? 'active' : ''}`}
                                disabled={!canEdit}
                                onClick={() => updateNestedField('inventory', 'closed', !formData.inventory?.closed)}
                            >
                                <span />
                            </button>
                        </div>
                    </div>
                )}

                {/* =================================================
                    PRICING
                ================================================= */}

                {activeTab === 'pricing' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Pricing</h3>

                            <p>Set the base price for this accommodation unit.</p>
                        </div>

                        <div className='form-grid'>
                            <div className='form-field'>
                                <label>Pricing Name</label>

                                <input
                                    type='text'
                                    value={formData.pricing?.name || ''}
                                    placeholder='Standard Rate'
                                    disabled={!canEdit}
                                    onChange={(e) => updateNestedField('pricing', 'name', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Currency</label>

                                <select
                                    value={formData.pricing?.currency || 'INR'}
                                    disabled={!canEdit}
                                    onChange={(e) => updateNestedField('pricing', 'currency', e.target.value)}
                                >
                                    <option value='INR'>INR - Indian Rupee</option>

                                    <option value='USD'>USD - US Dollar</option>

                                    <option value='AED'>AED - UAE Dirham</option>

                                    <option value='EUR'>EUR - Euro</option>
                                </select>
                            </div>

                            <div className='form-field'>
                                <label>Price Unit</label>

                                <select
                                    value={formData.pricing?.unit || 'per_night'}
                                    disabled={!canEdit}
                                    onChange={(e) => updateNestedField('pricing', 'unit', e.target.value)}
                                >
                                    <option value='per_night'>Per Night</option>

                                    <option value='per_person'>Per Person</option>

                                    {/*
                                     * Backend's PRICE_UNITS enum (listing.js)
                                     * only has "per_unit", not "per_room" -
                                     * sending "per_room" used to fail the
                                     * enum's silent-drop pattern; fixed to
                                     * the real accepted value, label kept
                                     * as "Per Room" since that's what this
                                     * option means for accommodation units.
                                     */}
                                    <option value='per_unit'>Per Room</option>

                                    <option value='per_bed'>Per Bed</option>

                                    <option value='per_package'>Per Package</option>
                                </select>
                            </div>

                            <div className='form-field'>
                                <label>Base Price</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.pricing?.actual ?? ''}
                                    placeholder='3500'
                                    disabled={!canEdit}
                                    onChange={(e) => updatePricing('actual', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Tax</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.pricing?.tax ?? ''}
                                    placeholder='0'
                                    disabled={!canEdit}
                                    onChange={(e) => updatePricing('tax', e.target.value)}
                                />
                                <small className='unit-field-hint'>{DEFAULT_TAX_RATE}% of base price, auto-filled. Editable.</small>
                            </div>

                            <div className='form-field'>
                                <label>Total Price</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.pricing?.total ?? ''}
                                    placeholder='3500'
                                    disabled={!canEdit}
                                    onChange={(e) => updatePricing('total', e.target.value)}
                                />
                            </div>

                            <div className='form-field'>
                                <label>Higher / Original Price</label>

                                <input
                                    type='number'
                                    min='0'
                                    value={formData.pricing?.higherPrice ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updatePricing('higherPrice', e.target.value)}
                                />
                                {originalPriceWarning(formData.pricing) && (
                                    <small className='unit-field-hint unit-field-warn'>
                                        {originalPriceWarning(formData.pricing)}
                                    </small>
                                )}
                            </div>

                            <div className='form-field'>
                                <label>Discount %</label>

                                <input
                                    type='number'
                                    min='0'
                                    max='100'
                                    value={formData.pricing?.discountPercent ?? ''}
                                    disabled={!canEdit}
                                    onChange={(e) => updatePricing('discountPercent', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className='pricing-summary'>
                            <span>Base Price</span>

                            <strong>₹{Number(formData.pricing?.actual || 0).toLocaleString('en-IN')}</strong>
                        </div>

                        {errors.pricingRules && <p className='unit-field-hint unit-field-warn'>{errors.pricingRules}</p>}
                        {/* weekend / season / early-bird prices for this unit */}
                        <UnitSpecialPrices
                            rules={formData.pricingRules}
                            basePrice={formData.pricing?.actual}
                            canEdit={canEdit}
                            onChange={(rules) => {
                                setFormData((prev) => ({ ...prev, pricingRules: rules }));
                                setErrors((prev) => ({ ...prev, pricingRules: '' }));
                            }}
                        />
                    </div>
                )}

                {/* =================================================
                    PHOTOS
                ================================================= */}

                {activeTab === 'photos' && (
                    <div className='unit-editor-section'>
                        <div className='unit-section-heading'>
                            <h3>Unit Photos</h3>

                            <p>Upload photos that belong specifically to this unit.</p>
                        </div>

                        {/* Upload header */}

                        <div className='unit-photo-toolbar'>
                            <div>
                                <strong>
                                    {formData.images?.length || 0} / {MAX_UNIT_IMAGES} photos
                                </strong>

                                <span>Add clear photos of the unit, bedroom, bathroom and view.</span>
                            </div>

                            <button
                                type='button'
                                className='small-primary-button'
                                disabled={
                                    !canEdit || isCompressing || (formData.images?.length || 0) >= MAX_UNIT_IMAGES
                                }
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {isCompressing ? 'Processing...' : '+ Add Photos'}
                            </button>

                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='image/*'
                                multiple
                                hidden
                                onChange={handleImageSelect}
                            />
                        </div>

                        {/* Error */}

                        {errors.images && <div className='field-error unit-photo-error'>{errors.images}</div>}

                        {/* Empty */}

                        {!formData.images?.length && !isCompressing && (
                            <div className='photo-upload-placeholder'>
                                <div>
                                    <span>📷</span>

                                    <h4>Add unit photos</h4>

                                    <p>
                                        Upload up to {MAX_UNIT_IMAGES} photos. The first photo will be used as the unit
                                        cover.
                                    </p>

                                    <button
                                        type='button'
                                        className='small-primary-button'
                                        disabled={!canEdit}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        + Add Photos
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Compressing */}

                        {isCompressing && (
                            <div className='unit-photo-processing'>
                                <div className='unit-photo-spinner' />

                                <div>
                                    <strong>Processing photos...</strong>

                                    <p>Optimizing images for faster uploads.</p>
                                </div>
                            </div>
                        )}

                        {/* Image grid */}

                        {formData.images?.length > 0 && (
                            <div className='unit-photo-grid'>
                                {formData.images.map((image, imageIndex) => {
                                    const imageUrl = getImageUrl(image);

                                    const existing = isExistingImage(image);

                                    return (
                                        <div
                                            key={`${imageUrl}-${imageIndex}`}
                                            className={`unit-photo-item ${
                                                draggedImageIndex === imageIndex ? 'dragging' : ''
                                            }`}
                                            draggable={canEdit}
                                            onDragStart={() => handleImageDragStart(imageIndex)}
                                            onDragOver={handleImageDragOver}
                                            onDrop={() => handleImageDrop(imageIndex)}
                                            onDragEnd={() => setDraggedImageIndex(null)}
                                        >
                                            <div className='unit-photo-image'>
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${formData.name || 'Unit'} ${imageIndex + 1}`}
                                                    />
                                                ) : (
                                                    <div className='unit-photo-broken'>No preview</div>
                                                )}

                                                {imageIndex === 0 && <span className='unit-cover-badge'>Cover</span>}

                                                <span className='unit-photo-number'>{imageIndex + 1}</span>

                                                {!existing && <span className='unit-new-image-badge'>New</span>}
                                            </div>

                                            <div className='unit-photo-actions'>
                                                <button
                                                    type='button'
                                                    disabled={!canEdit || imageIndex === 0}
                                                    onClick={() => moveImage(imageIndex, 'left')}
                                                    title='Move left'
                                                >
                                                    ←
                                                </button>

                                                <button
                                                    type='button'
                                                    disabled={!canEdit || imageIndex === formData.images.length - 1}
                                                    onClick={() => moveImage(imageIndex, 'right')}
                                                    title='Move right'
                                                >
                                                    →
                                                </button>

                                                <button
                                                    type='button'
                                                    className='danger'
                                                    disabled={!canEdit}
                                                    onClick={() => removeImage(imageIndex)}
                                                    title='Remove photo'
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Guidelines */}

                        <div className='unit-photo-guidelines'>
                            <strong>Photo guidelines</strong>

                            <ul>
                                <li>Use landscape or high-quality photos where possible.</li>

                                <li>The first photo becomes the unit cover.</li>

                                <li>Drag photos to reorder them.</li>

                                <li>Existing photos are preserved during edit.</li>

                                <li>New photos will be uploaded to Firebase when the accommodation is submitted.</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* =================================================
                FOOTER
            ================================================= */}

            <div className='unit-editor-footer'>
                <button type='button' className='editor-secondary-button' onClick={onCancel}>
                    Cancel
                </button>

                {canEdit && (
                    <button
                        type='button'
                        className='editor-primary-button'
                        onClick={handleSave}
                        disabled={isCompressing}
                    >
                        {index !== null ? 'Save Unit Changes' : 'Add Unit'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default UnitEditor;
